/**
 * Turning an audit row into something a person can read (§§4.36, 11.53).
 *
 * The database stores what changed, honestly and generically:
 *
 *     { action: 'UPDATE', old_values: { city: 'Bengaluru', availability: 'WEEKENDS' },
 *                         new_values: { city: 'Mysuru',    availability: 'WEEKENDS,FLEXIBLE' } }
 *
 * That is the right thing to *store* and the wrong thing to *show*. §11.53's demo
 * moment is a member of staff pointing at a record and asking "who changed this,
 * and why?" — and being answered, not handed a column name and a JSON blob.
 *
 * So this module does three things the raw row cannot:
 *
 *   **Names the field in English.** `availability_notes` is a column; "Availability
 *   notes" is what it is called on the screen it was edited on.
 *
 *   **Translates codes.** `WEEKENDS,FLEXIBLE` is stored; "Weekends, Flexible" is
 *   what the person who ticked those boxes saw. A history that shows codes makes
 *   staff translate the system's language back into their own, every time.
 *
 *   **Formats by type.** Minutes become "2h 30m", dates become "9 Aug 2026", an
 *   empty value becomes "not set" rather than a blank space that reads as a
 *   rendering bug.
 *
 * What it deliberately does *not* do is interpret. The audit row is the record;
 * this only makes it legible.
 */

import { ageGroupLabel, formatDuration, occupationLabel, splitCodes } from '@parinaam-vms/shared';
import type { AuditEntry, ReferenceValue } from '@parinaam-vms/shared';
import { formatDate, humaniseCode } from '../../utils/format';

/** One field that moved, ready to render. */
export interface FieldChange {
  label: string;
  from: string;
  to: string;
}

type Reference = Record<string, ReferenceValue[]> | undefined;

/**
 * What each action *did*, in the past tense, from the point of view of someone
 * who was not there.
 *
 * Keyed on the action rather than composed from the field list, because the
 * interesting ones are not field edits at all: `OVERLAP_ACCEPTED` records a
 * judgement, not a value.
 */
const ACTION_SUMMARY: Record<string, string> = {
  CREATE: 'Created',
  UPDATE: 'Details edited',
  STATUS_CHANGE: 'Status changed',
  APPROVE: 'Approved',
  REJECT: 'Rejected',
  CANCEL: 'Cancelled',
  CORRECTION: 'Hours corrected',
  OVERRIDE: 'Hours overridden',
  DELETE: 'Deleted',
  ATTENDANCE_WITHOUT_ASSIGNMENT: 'Recorded without an assignment',
  OVERLAP_ACCEPTED: 'Double-booking accepted',
};

/**
 * The column names staff never see, mapped to the labels they do.
 *
 * Anything absent falls back to `humaniseCode`, which turns `postal_code` into
 * "Postal code" — good enough for the long tail, and wrong often enough for the
 * fields that matter that the common ones are named here explicitly.
 */
const FIELD_LABEL: Record<string, string> = {
  areas_of_interest: 'Areas of interest',
  // `availability` and `availability_notes` are gone from every form, but their
  // labels stay: this file renders HISTORY, and a volunteer edited in August has
  // audit rows naming both. Dropping them here would not hide those rows, only
  // leave `humaniseCode` to render "Availability" where the field was called
  // "Available". (The CODES on those rows do degrade — migration 038 retires the
  // AVAILABILITY pick-list, and the API serves active values only, so they fall
  // back to `humaniseCode` too. Cosmetic, and on archived entries alone.)
  availability: 'Available',
  availability_notes: 'Availability notes',
  duration_minutes: 'Duration',
  attendance_date: 'Date attended',
  assignment_status: 'Assignment',
  override_flag: 'Marked as adjusted',
  skills: 'Notes',
  organization: 'Organisation',
};

/** Which fields hold `reference_values` codes, and from which category. */
const CODE_CATEGORY: Record<string, string> = {
  gender: 'GENDER',
  languages: 'LANGUAGE',
  areas_of_interest: 'AREA_OF_INTEREST',
  availability: 'AVAILABILITY',
};

/**
 * Fields recorded for context rather than because they changed.
 *
 * `attendance.correct` always writes `duration_minutes` and `status`, changed or
 * not, because the `attendance_audit` view projects them into typed columns
 * (Plan D7). Showing "Status: Approved → Approved" would be honest about the
 * payload and misleading about the event.
 */
export function actualChanges(entry: AuditEntry, reference: Reference): FieldChange[] {
  const before = entry.old_values ?? {};
  const after = entry.new_values ?? {};

  // Every field named on either side. A CREATE has only `new_values`; a DELETE
  // may have only `old_values`.
  const fields = [...new Set([...Object.keys(before), ...Object.keys(after)])];

  return fields
    .filter((field) => !same(before[field], after[field]))
    .map((field) => ({
      label: FIELD_LABEL[field] ?? humaniseCode(field),
      from: display(field, before[field], reference),
      to: display(field, after[field], reference),
    }));
}

/** The one-line summary of what happened. */
export function summarize(entry: AuditEntry): string {
  return ACTION_SUMMARY[entry.action] ?? humaniseCode(entry.action);
}

/**
 * One value, as the person who entered it would recognise it.
 *
 * The type is inferred from the field name rather than from the value, because
 * the value arrives as JSON and `150` alone cannot tell you whether it is minutes
 * or a house number.
 */
function display(field: string, value: unknown, reference: Reference): string {
  if (value === null || value === undefined || value === '') return 'not set';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';

  if (field === 'duration_minutes' && typeof value === 'number') {
    return formatDuration(value);
  }

  if (field === 'attendance_date') {
    return formatDate(String(value));
  }

  // Occupation and age group are fixed codes (migrations 036/037), not reference
  // values, so they are labelled from the shared maps rather than looked up.
  if (field === 'occupation') return occupationLabel(String(value));
  if (field === 'age_group') return ageGroupLabel(String(value));

  const category = CODE_CATEGORY[field];
  if (category) return labelsFor(reference, category, String(value));

  // Statuses are codes too, but they live in CHECK constraints rather than the
  // reference table (Plan D5), so they are humanised rather than looked up.
  if (field.endsWith('status')) return humaniseCode(String(value));

  // Objects are the conflict lists on an OVERLAP_ACCEPTED row, which the reason
  // already spells out in a sentence. Rendering `[object Object]` would be worse
  // than saying nothing.
  if (typeof value === 'object') return '—';

  return String(value);
}

/** Comma-separated codes as their labels, in the order they were stored. */
function labelsFor(reference: Reference, category: string, value: string): string {
  const values = reference?.[category] ?? [];
  const labels = splitCodes(value).map(
    (code) => values.find((option) => option.code === code)?.label ?? humaniseCode(code)
  );
  return labels.length > 0 ? labels.join(', ') : 'not set';
}

/** Treats absent and null as the same thing, which for a JSON payload they are. */
function same(before: unknown, after: unknown): boolean {
  return (before ?? null) === (after ?? null);
}
